/* eslint-disable import/no-cycle */
import React from 'react'

import { useFormik, yupToFormErrors } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { withTheme } from 'styled-components'

import {
  corporateCredentialRecoveryClear,
  recoveryCorporateCredentials
} from 'actions/corporate/credentialsRecovery'
import Button from 'components/button'
import { Title5 } from 'components/fonts'
import Icon, { IconNames } from 'components/icons'
import Input from 'components/input'
import { TransitionLoader } from 'components/loaders'
import Message from 'components/message'
import Modal from 'components/modal'
import {
  ModalWrapper,
  ModalHeaderWrapper,
  ModalContentWrapper,
  InteractiveIconContainer
} from 'containers/corporate-agreements/login-container/login/credentials-recovery/styled'
import {
  Label,
  FormContainer,
  ButtonWrapper,
  StyledLink,
  RecoveryLinkContainer
} from 'containers/corporate-agreements/login-container/login/styled'
import {
  FeedbackContainer,
  FeedbackTitle
} from 'containers/corporate-agreements/login-container/styled'
import e2eClasses from 'helpers/automation'
import NAMESPACES from 'localization/constants/namespaces'
import {
  isFetchingCredentialRecovery,
  getCredentialRecoveryError,
  getCredentialRecoverySuccess
} from 'selectors/corporate/credentialsRecovery'

import { recoveryValidationSchema } from './credentials-recovery-validation-schema'

const getFeedbackContent = (requestError, t) => (
  <FeedbackTitle margin="0 15px 0 0">
    {requestError
      ? t('agreements.login.forgot-error', { context: requestError.statusCode })
      : t('agreements.login.forgot-success')}
  </FeedbackTitle>
)

const initialValues = { credential: '' }

const CredentialRecoveryModal = props => {
  const { t } = useTranslation(NAMESPACES.CORPORATE_AGREEMENTS)

  /**
   * Resets form everytime success object changes, due to immutability,
   * this would be anytime api request returns a 2XX http code
   */
  React.useEffect(() => {
    formik.resetForm()
  }, [props.requestSuccess])

  const validationHandler = formikValues =>
    recoveryValidationSchema(t)
      .validate(formikValues, {
        abortEarly: false,
        context: {
          credentialToRecover: props.credentialType
        }
      })
      .then(() => ({}))
      .catch(err => yupToFormErrors(err))

  const submitHandler = ({ credential }) => {
    props.recoveryCorporateCredentials(credential, props.credentialType)
  }

  const formik = useFormik({
    initialValues,
    validate: validationHandler,
    onSubmit: submitHandler
  })

  const onClose = () => {
    props.corporateCredentialRecoveryClear()
    formik.resetForm()
    props.onClose()
  }

  const onToggleAction = () => {
    formik.resetForm()
    props.toggleCredentialType()
  }

  const getInputProps = () => {
    const inputProps = {
      name: 'credential',
      type: 'text',
      placeholder: t('agreements.login.document-placeholder'),
      smallPadding: true,
      light: true,
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
      value: formik.values.credential,
      error: formik.touched.credential && formik.errors.credential
    }
    if (props.credentialType === 'password') {
      inputProps.type = 'text'
      inputProps.placeholder = t('agreements.login.username-placeholder')
    }
    return inputProps
  }

  const getUILabels = () => {
    const labels = {
      inputLabel: t('agreements.login.document-label'),
      submitButtonLabel: t('agreements.login.forgot-submit', {
        credentialName: t('agreements.login.username-label')
      }),
      toggleActionLabel: t('agreements.login.forgot-password.toggle')
    }
    if (props.credentialType === 'password') {
      labels.inputLabel = t('agreements.login.username-label')
      labels.submitButtonLabel = t('agreements.login.forgot-submit', {
        credentialName: t('agreements.login.password-label')
      })
      labels.toggleActionLabel = t('agreements.login.forgot-username.toggle')
    }
    return labels
  }

  const { inputLabel, submitButtonLabel, toggleActionLabel } = getUILabels()

  const getModalContent = () => (
    <React.Fragment>
      {props.isLoading && <TransitionLoader />}
      {(props.requestSuccess || props.requestError) && (
        <FeedbackContainer noMargin>
          <Message
            type={props.requestError ? 'error' : 'success'}
            onMessageButtonClick={props.corporateCredentialRecoveryClear}
            messageButtonText={t('agreements.request.feedback-button-text')}
          >
            {getFeedbackContent(props.requestError, t)}
          </Message>
        </FeedbackContainer>
      )}
      <form onSubmit={formik.handleSubmit}>
        <FormContainer padding="0">
          <Label e2eClass={e2eClasses('corporate.userDocument')}>{inputLabel}</Label>
          <Input e2eClass={e2eClasses('corporate.userDocument')} {...getInputProps()} />
          <RecoveryLinkContainer>
            <StyledLink href="#" onClick={onToggleAction}>
              {toggleActionLabel}
            </StyledLink>
          </RecoveryLinkContainer>
        </FormContainer>
        <ButtonWrapper>
          <Button e2eClass={e2eClasses('corporate.recoverButton')} type="submit">
            {submitButtonLabel}
          </Button>
        </ButtonWrapper>
      </form>
    </React.Fragment>
  )

  const getModalComponent = () => (
    <ModalWrapper>
      <ModalHeaderWrapper autoHeight minHeight="10vh" alignItems="center">
        <Title5 margin="0">{t('agreements.login.forgot-title')}</Title5>
        <InteractiveIconContainer onClick={onClose} alignSelf="center">
          <Icon name={IconNames.Close} color={props.theme.colors.blue50} size={24} />
        </InteractiveIconContainer>
      </ModalHeaderWrapper>
      <ModalContentWrapper margin="0 0 20px 0">{getModalContent()}</ModalContentWrapper>
    </ModalWrapper>
  )

  return (
    <Modal name={props.modalName} onClose={onClose} autoWidth>
      {getModalComponent()}
    </Modal>
  )
}

CredentialRecoveryModal.propTypes = {
  modalName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  toggleCredentialType: PropTypes.func.isRequired,
  credentialType: PropTypes.oneOf(['userEmail', 'password']).isRequired
}

const mapStateToProps = state => ({
  isLoading: isFetchingCredentialRecovery(state),
  requestSuccess: getCredentialRecoverySuccess(state),
  requestError: getCredentialRecoveryError(state)
})

const mapDispatchToProps = {
  corporateCredentialRecoveryClear,
  recoveryCorporateCredentials
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(CredentialRecoveryModal))
